import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { reportsService } from '../services'
import { getApiErrorMessage } from '../services/apiClient'
import { VIEWER_TIMEZONE } from '../hooks/useDashboard'
import { formatDate } from '../utils/dateFormat'
import { formatNumber } from '../components/Dashboard/dashboardFormatters'
import { buildDashboardReturnSearch } from '../utils/undeliveredQuantityFilters'
import TableEmptyState from '../components/shared/TableEmptyState'
import TableLoadingRow from '../components/shared/TableLoadingRow'

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: '3d', label: 'Last 3 Days' },
  { value: '7d', label: 'Last 7 Days' },
  { value: 'custom', label: 'Custom Range' },
]

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100]

const headerCellSx = { fontWeight: 600, color: '#1a1a1a' }

const paginationSx = {
  width: '100%',
  overflowX: 'auto',
  '& .MuiToolbar-root': {
    flexWrap: 'wrap',
    justifyContent: { xs: 'center', sm: 'flex-end' },
    minHeight: { xs: 48, sm: 52 },
    px: { xs: 0.5, sm: 2 },
    gap: 0.5,
  },
}

const formatQty = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 })
}

const UndeliveredQuantityPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isCompact = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const [searchParams, setSearchParams] = useSearchParams()

  const datePreset = searchParams.get('datePreset') || '7d'
  const dateFrom = searchParams.get('dateFrom') || ''
  const dateTo = searchParams.get('dateTo') || ''
  const shop = searchParams.get('shop') || ''
  const orderId = searchParams.get('orderId') || ''
  const articleCode = searchParams.get('articleCode') || ''
  const page = Math.max(0, parseInt(searchParams.get('page') || '0', 10) || 0)
  const rowsPerPage = ROWS_PER_PAGE_OPTIONS.includes(
    parseInt(searchParams.get('limit') || '10', 10),
  )
    ? parseInt(searchParams.get('limit') || '10', 10)
    : 10
  const locationIds = searchParams.get('locationIds') || ''
  const categoryIds = searchParams.get('categoryIds') || ''
  const organizationIds = searchParams.get('organizationIds') || ''

  const [shopInput, setShopInput] = useState(shop)
  const [orderInput, setOrderInput] = useState(orderId)
  const [articleInput, setArticleInput] = useState(articleCode)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [totalQuantity, setTotalQuantity] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setShopInput(shop)
  }, [shop])
  useEffect(() => {
    setOrderInput(orderId)
  }, [orderId])
  useEffect(() => {
    setArticleInput(articleCode)
  }, [articleCode])

  const replaceParams = useCallback(
    (updates) => {
      const next = new URLSearchParams(searchParams)
      Object.entries(updates).forEach(([key, value]) => {
        if (value == null || value === '') next.delete(key)
        else next.set(key, String(value))
      })
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      if (shopInput === shop && orderInput === orderId && articleInput === articleCode) return
      replaceParams({
        shop: shopInput.trim(),
        orderId: orderInput.trim(),
        articleCode: articleInput.trim(),
        page: 0,
      })
    }, 350)
    return () => clearTimeout(timer)
  }, [shopInput, orderInput, articleInput, shop, orderId, articleCode, replaceParams])

  const customReady =
    datePreset !== 'custom' || (Boolean(dateFrom) && Boolean(dateTo) && dateFrom <= dateTo)

  const scopeActive = Boolean(locationIds || categoryIds || organizationIds)

  const queryKey = useMemo(
    () =>
      [
        locationIds,
        categoryIds,
        organizationIds,
        datePreset,
        dateFrom,
        dateTo,
        shop,
        orderId,
        articleCode,
        page,
        rowsPerPage,
      ].join('|'),
    [
      locationIds,
      categoryIds,
      organizationIds,
      datePreset,
      dateFrom,
      dateTo,
      shop,
      orderId,
      articleCode,
      page,
      rowsPerPage,
    ],
  )

  useEffect(() => {
    if (!customReady) {
      setLoading(false)
      setRows([])
      setTotal(0)
      setTotalQuantity(0)
      return undefined
    }
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    const params = {
      viewerTimezone: VIEWER_TIMEZONE,
      datePreset,
      page: page + 1,
      limit: rowsPerPage,
      signal: controller.signal,
    }
    if (locationIds) params.locationIds = locationIds
    if (categoryIds) params.categoryIds = categoryIds
    if (organizationIds) params.organizationIds = organizationIds
    if (datePreset === 'custom') {
      params.dateFrom = dateFrom
      params.dateTo = dateTo
    }
    if (shop) params.shop = shop
    if (orderId) params.orderId = orderId
    if (articleCode) params.articleCode = articleCode

    reportsService
      .getUndeliveredLines(params)
      .then((data) => {
        setRows(Array.isArray(data?.rows) ? data.rows : [])
        setTotal(Number(data?.pagination?.total) || 0)
        setTotalQuantity(Number(data?.totalQuantity) || 0)
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return
        setRows([])
        setTotal(0)
        setTotalQuantity(0)
        setError(getApiErrorMessage(err, 'Failed to load undelivered quantities'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [queryKey, customReady, locationIds, categoryIds, organizationIds, datePreset, dateFrom, dateTo, shop, orderId, articleCode, page, rowsPerPage])

  const handleRowClick = (rowOrderId) => {
    navigate(`/orders/${rowOrderId}`, {
      state: {
        ordersListPath: `${location.pathname}${location.search}`,
      },
    })
  }

  const handleBack = () => {
    if (location.state?.dashboardSearch != null) {
      const search = location.state.dashboardSearch
      navigate(search ? `/dashboard?${search}` : '/dashboard')
      return
    }
    const search = buildDashboardReturnSearch({
      locationIds,
      categoryIds,
      organizationIds,
      datePreset,
      dateFrom,
      dateTo,
    })
    navigate(search ? `/dashboard?${search}` : '/dashboard')
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          color="inherit"
          sx={{ textTransform: 'none' }}
        >
          Dashboard
        </Button>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-end' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Undelivered Quantity
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Line items not delivered on purchase orders closed with partial delivery.
            {customReady ? ` Total ${formatNumber(totalQuantity)}.` : ''}
          </Typography>
        </Box>
      </Stack>

      {scopeActive && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() =>
                replaceParams({
                  locationIds: '',
                  categoryIds: '',
                  organizationIds: '',
                  page: 0,
                })
              }
            >
              Clear scope
            </Button>
          }
        >
          Limited to the dashboard location, category, and partner shop filters.
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'flex-end' }}
            flexWrap="wrap"
            useFlexGap
          >
            <TextField
              label="Shop name / ID"
              size="small"
              value={shopInput}
              onChange={(event) => setShopInput(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 200 }, flex: 1 }}
            />
            <TextField
              label="Order ID"
              size="small"
              value={orderInput}
              onChange={(event) => setOrderInput(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 140 } }}
            />
            <TextField
              label="Article code"
              size="small"
              value={articleInput}
              onChange={(event) => setArticleInput(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 160 } }}
            />
            <TextField
              select
              label="Date"
              size="small"
              value={datePreset}
              onChange={(event) => {
                const next = event.target.value
                replaceParams({
                  datePreset: next,
                  dateFrom: next === 'custom' ? dateFrom : '',
                  dateTo: next === 'custom' ? dateTo : '',
                  page: 0,
                })
              }}
              sx={{ minWidth: 170 }}
            >
              {DATE_PRESETS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {datePreset === 'custom' && (
              <>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={dateFrom}
                  onChange={(event) =>
                    replaceParams({ dateFrom: event.target.value, page: 0 })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={dateTo}
                  onChange={(event) =>
                    replaceParams({ dateTo: event.target.value, page: 0 })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                />
              </>
            )}
          </Stack>
          {datePreset === 'custom' && !customReady && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Choose a start and end date. The start must be on or before the end.
            </Typography>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card
        sx={{
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <TableContainer
          sx={{
            width: '100%',
            minWidth: 0,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Table sx={{ minWidth: 880 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                <TableCell sx={headerCellSx}>Article Code</TableCell>
                <TableCell sx={headerCellSx}>Article Name</TableCell>
                <TableCell sx={headerCellSx}>Order ID</TableCell>
                <TableCell sx={headerCellSx}>Shop Name</TableCell>
                <TableCell sx={headerCellSx} align="right">
                  Quantity (undelivered)
                </TableCell>
                <TableCell sx={headerCellSx}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={6} />
              ) : rows.length === 0 ? (
                <TableEmptyState colSpan={6} message="No undelivered line items found." />
              ) : (
                rows.map((row, index) => (
                  <TableRow
                    key={`${row.orderId}-${row.articleCode || 'line'}-${index}`}
                    onClick={() => handleRowClick(row.orderId)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#f8f9fb' },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {row.articleCode || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.articleName || '—'}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.orderId}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      {row.shopName || (row.shopId != null ? String(row.shopId) : '—')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatQty(row.quantity)}
                    </TableCell>
                    <TableCell>{formatDate(row.date)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, nextPage) => replaceParams({ page: nextPage })}
          onRowsPerPageChange={(event) =>
            replaceParams({
              limit: parseInt(event.target.value, 10),
              page: 0,
            })
          }
          labelRowsPerPage={isCompact ? 'Rows' : 'Rows per page:'}
          sx={paginationSx}
        />
      </Card>
    </Box>
  )
}

export default UndeliveredQuantityPage
